import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { proxyAPI } from "../proxyAPI";
import ContactSetEditor from "./ContactSetEditor";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import ContactsIcon from "../../assets/icons/contact_edit.svg2";
import PageHeader from "../Common/PageHeader";
import { isUserPermitted } from "../helpers/pageProps";
import contactUUID from "./contactUUID";
import Logs from "../Dashboard/Logs";
import HelpButton from "../HelpContent/HelpButton";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export default function ContactSet({ uuid, isModal = false }) {
  const navigate = useNavigate();
  const confirmDialog = useConfirmDialog();
  const [loading, setLoading] = useState(false);
  const [contactSet, setContactSet] = useState(null);
  const [sendList, setSendList] = useState({
    clearStatic: false,
    add: [],
    remove: [],
  });
  const [showFilterErrors, setShowFilterErrors] = useState(false);

  const getContactSet = async () => {
    if (uuid === "new") {
      const newUUID = contactUUID();
      setContactSet({
        uuid: newUUID,
        label: "New group",
        filters: [],
        type: "dynamic",
      });
      return;
    }

    const data = await proxyAPI({
      prefix: "contacts/v1",
      url: `/contact-set/${uuid}`,
      setLoading,
    });
    if (data.uuid) {
      setContactSet(data);
    } else if (data.message) {
      toast.error(data.message);
    }
  };

  useEffect(() => {
    // set contactSet when the window shows
    if (uuid) {
      getContactSet();
    }
  }, [uuid]);

  const isFiltersValid = () => {
    const errors = [];
    contactSet?.filters?.forEach((f) => {
      if (
        !["empty", "not-empty"].includes(f.operator) &&
        (f.values.some((v) => !v.trim()) || f.values.length === 0)
      ) {
        errors.push("No value chosen in the filter. ");
      }
      if (f.fields.length === 0) {
        errors.push("No fields chosen in the filter. ");
      }
    });
    return [...new Set(errors)];
  };

  const deleteContactSet = async () => {
    if (
      !(await confirmDialog(
        "Delete group",
        `Are you sure you want to delete this group?`
      ))
    ) {
      return;
    }
    const data = await proxyAPI({
      prefix: "contacts/v1",
      url: `/delete-groups`,
      method: "POST",
      body: {
        uuid: [uuid],
      },
    });
    if (data?.isError) {
      toast.error(data?.message || "Error deleting group");
      return;
    }
    toast.success("Delete request added to the queue.");
    navigate("/assets/contacts/groups");
  };

  const saveContactSet = async () => {
    const filterErrors = isFiltersValid();
    if (filterErrors.length > 0) {
      setShowFilterErrors(true);
      return;
    }

    const bodyContent = {
      contact_set: contactSet,
      clearStatic: sendList.clearStatic,
      add: sendList.add,
      remove: sendList.remove,
    };

    const data = await proxyAPI({
      prefix: "contacts/v1",
      method: "POST",
      url: "/contact-set",
      body: bodyContent,
    });

    if (data) {
      toast.success("saved successfully");
      setSendList({
        clearStatic: false,
        add: [],
        remove: [],
      });
      if (!isModal) {
        navigate(`/assets/contacts/groups/${bodyContent.contact_set.uuid}`, {
          replace: true,
        });
      }
    }
  };

  return (
    <>
      <PageHeader
        Icon={ContactsIcon}
        heading="Contact group"
        subheading="Manage your Contact Group."
      >
        <div className="d-flex flex-wrap gap-2" />
      </PageHeader>

      {!loading &&
        (contactSet ? (
          <>
            {!isUserPermitted("edit_group") && (
              <div className="alert alert-warning text-center">
                You do not have permission to edit this group
              </div>
            )}
            <fieldset disabled={!isUserPermitted("edit_group")}>
              <div className="row mb-4">
                <div className="col">
                  <div className="input-group" style={{ height: 40 }}>
                    <label className="input-group-text">Group label</label>
                    <input
                      className="form-control"
                      title="Group label"
                      placeholder="Group label"
                      type="text"
                      value={contactSet?.label ?? ""}
                      onChange={(e) =>
                        setContactSet((prev) => ({
                          ...prev,
                          label: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="col-lg-5">
                  <div className="input-group">
                    <label className="input-group-text">
                      Group type <HelpButton helpKey="group-type" />
                    </label>
                    <div className="flex-grow-1">
                      <Input
                        inputType="select"
                        isValidNewOption={() => false}
                        value={[contactSet?.type]}
                        onChange={(v) => {
                          setContactSet((prev) => ({ ...prev, type: v[0] }));
                        }}
                        options={[
                          {
                            value: "dynamic",
                            label: "Dynamically create your group",
                          },
                          { value: "static", label: "Static group" },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-4">
                <div className="col">
                  <div className="input-group">
                    <label className="input-group-text">
                      Group Description
                    </label>
                    <div className="flex-grow-1">
                      <Input
                        value={[contactSet?.description]}
                        onChange={(v) => {
                          setContactSet((prev) => ({
                            ...prev,
                            description: v[0],
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-5">
                  <div className="input-group">
                    <label className="input-group-text">Group Reference</label>
                    <div className="flex-grow-1">
                      <Input
                        value={[contactSet?.reference]}
                        onChange={(v) => {
                          setContactSet((prev) => ({
                            ...prev,
                            reference: v[0],
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            <ContactSetEditor
              contactSet={contactSet}
              setContactSet={setContactSet}
              key={contactSet?.uuid}
              sendList={sendList}
              setSendList={setSendList}
              filterErrors={isFiltersValid()}
              showFilterErrors={showFilterErrors}
            />
            <JsonCollapse json={{ contactSet, sendList }} />
          </>
        ) : (
          <div className="alert alert-danger text-center">Group not found</div>
        ))}
      {uuid && uuid !== "new" && (
        <>
          <h5 className="mt-5">
            {/* <CommFieldsIcon width={30} className="me-2" /> */}
            Logs
          </h5>
          <hr className="my-3 text-secondary" />
          <div className="p-3 bg-white rounded-3">
            <Logs kind="group" resource={uuid} />
          </div>
        </>
      )}
      <div className="d-flex justify-content-end mt-2 mb-5 gap-3">
        <div className="col-lg-1">
          <button
            type="button"
            onClick={() => deleteContactSet(contactSet.uuid)}
            className="btn btn-colored form-control fw-bold"
          >
            Delete
          </button>
        </div>
        <div className="col-lg-1">
          <button
            type="button"
            className="btn btn-colored form-control fw-bold"
            onClick={saveContactSet}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
}
